import { EMPTY_URL } from "csi/constants";
import csi from "csi/main";
import DefaultsConstructor, { Defaults } from "csi/prefs/Defaults";
import { sessionStore } from "csi/state/rootStore";
import { ILocaleProvider } from "designSystem/locale";
import { keyBy } from "lodash";
import { observable } from "mobx";
import { REQUEST_MODULE, REQUEST_OPERATION, createFormData, postFormData } from "src/c8-platform/core/request";
import { ICustomViewInfoResponse } from "src/c8-platform/service/customViewBuilderService";
import { cnl } from "src/c8-platform/type/value";
import CustomViewDialogStore from "../CustomViewDialogStore";
import { ShareViewWith } from "../options/CustomViewOptions";
import { ViewCategory } from "./CustomViewDialogLeftPanelAccordion";
import { CustomViewItem } from "./CustomViewItem";
import { CustomViewNodeContexts } from "./CustomViewNodeContexts";
import { CustomViewStore } from "./CustomViewStore";

export class NonGlobalCustomView {
	/** custom view dialog store */
	readonly #customViewDialogStore: CustomViewDialogStore;

	/** name of the owner, either role or user */
	readonly name: string;

	/** url of the owner, either role or user */
	readonly url: cnl;

	/** overall items of the custom views including names */
	readonly viewItems = observable.array<CustomViewItem>([]);

	/** filtered custom view items */
	get filteredViewItems(): CustomViewItem[] {
		const { searchText } = this.#customViewDialogStore.customViewStore;
		return this.viewItems.filter((view) => view.searchViewByName(searchText));
	}

	constructor(url: cnl, name: string, viewItems: CustomViewItem[], customViewDialogStore: CustomViewDialogStore) {
		this.url = url;
		this.name = name;
		this.viewItems.replace(viewItems);
		this.#customViewDialogStore = customViewDialogStore;
	}
}

/** save favorite */
export async function saveFavorite(containerUrl: cnl, customViewUrl: cnl): Promise<Set<cnl>> {
	const formData = createFormData({
		[REQUEST_MODULE]: "SiteAdmin",
		[REQUEST_OPERATION]: "SaveFavoriteCustomView",
		ContainerURL: containerUrl,
		User: sessionStore.userUrl,
		PreferenceView: customViewUrl,
	});

	const favoriteURLs = await postFormData<cnl[]>(formData);
	return new Set(favoriteURLs);
}


/** remove favorite */
export async function removeFavorite(containerUrl: cnl, customViewUrl: cnl): Promise<Set<cnl>> {
	const formData = createFormData({
		[REQUEST_MODULE]: "SiteAdmin",
		[REQUEST_OPERATION]: "RemoveFavoriteCustomView",
		ContainerURL: containerUrl,
		User: sessionStore.userUrl,
		PreferenceView: customViewUrl,
	});

	const favoriteURLs = await postFormData<cnl[]>(formData);
	return new Set(favoriteURLs);
}

/** generate default custom views for the subtyped views */
export function generateDefaultCustomViewForSubtypedViews(
	customViewContext: string,
	customViewNodeContext: CustomViewNodeContexts,
	defaults: Defaults,
	roleCnl: cnl,
	roleBasedCustomViewSet: Set<cnl>,
	names: Record<cnl, string>,
	loc: ILocaleProvider,
): CustomViewItem[] {
	const defaultCustomViews: CustomViewItem[] = [];
	const viewMap = defaults.getSubtypedList(customViewContext, roleCnl);
	const { contextType, forState } = customViewNodeContext;

	const all = !forState ? loc.resource("CustomViewAllSubtypedDefaultView", [loc.type(contextType, true)]) : loc.resource("CustomViewAllStatesDefaultView");

	for (const [url, subtypes] of Object.entries(viewMap)) {
		const category = roleBasedCustomViewSet.has(url) ? ViewCategory.ROLE_BASED : ViewCategory.PUBLIC;

		if (!url) {
			// the view doesn't setup yet, just skip
			continue;
		}

		const name = names[url];
		let group = subtypes.join(", ");

		// handle the global default view
		if (subtypes.length == 0) {
			group = all;

			// global is always on the top
			defaultCustomViews.unshift(new CustomViewItem(
				url,
				name,
				category,
				null,
				group,
			));
		} else {
			defaultCustomViews.push(new CustomViewItem(
				url,
				name,
				category,
				null,
				group,
			));
		}
	}

	// no default view yet, use the OOTB one
	if (defaultCustomViews.length === 0) {
		defaultCustomViews.push(new CustomViewItem(
			EMPTY_URL,
			loc.resource("ViewDefault"),
			ViewCategory.PUBLIC,
			null,
			all,
		));
	}

	return defaultCustomViews;
}

/** generate whole list of default custom views, including subtyped and non-subtyped */
export function generateDefaultCustomViewInfo(
	roleCnl: cnl,
	customViewContext: string,
	customViewNodeContext: CustomViewNodeContexts,
	names: Record<cnl, string>,
	loc: ILocaleProvider,
	defaults: Defaults,
	roleBasedCustomViewSet?: Set<cnl>,
): CustomViewItem[] {
	let defaultCustomViews: CustomViewItem[] = [];
	const { hasMultiple: isSubtypedView } = customViewNodeContext;

	if (roleCnl) {
		// role based view
		let category = ViewCategory.ROLE_BASED;
		let url = defaults.get(customViewContext, roleCnl, true);

		if (!isSubtypedView) {
			if (!url) {
				// if no default yet, use the global one
				url = defaults.get(customViewContext, csi.EMPTY_URL);
				category = ViewCategory.PUBLIC;
			}

			const name = names[url];

			defaultCustomViews.push(new CustomViewItem(
				url,
				name,
				category,
			));
		} else {
			// subtyped view
			defaultCustomViews = generateDefaultCustomViewForSubtypedViews(
				customViewContext,
				customViewNodeContext,
				defaults,
				roleCnl,
				roleBasedCustomViewSet,
				names,
				loc,
			);
		}
	} else {
		// global default view
		if (!isSubtypedView) {
			const url = defaults.get(customViewContext, csi.EMPTY_URL);
			const name = names[url];

			defaultCustomViews.push(new CustomViewItem(
				url,
				name,
				ViewCategory.PUBLIC,
			));
		} else {
			defaultCustomViews = generateDefaultCustomViewForSubtypedViews(
				customViewContext,
				customViewNodeContext,
				defaults,
				null,
				roleBasedCustomViewSet,
				names,
				loc,
			);
		}
	}

	return defaultCustomViews;
}

/** convert the response to custom view object */
export function getCustomViewResult(response: ICustomViewInfoResponse, selectedCustomViewCnl: cnl, customViewDialogStore: CustomViewDialogStore): CustomViewStore {
	const { names, descriptions, favorites, systemWide, roles, personal } = response;
	const { userUrl, userName } = sessionStore;

	const systemWideViewsDictionary = keyBy(systemWide, (url) => url);
	const personalViewsDictionary = keyBy(personal, ({ url }) => url);
	const roleItemSets = new Set<cnl>();
	roles.forEach((role) => role.views.forEach((url) => roleItemSets.add(url)));

	const favoriteSets = new Set<cnl>();
	const favoriteItems: CustomViewItem[] = favorites.map((url) => {
		favoriteSets.add(url);
		let category = ViewCategory.PERSONAL;

		if (systemWideViewsDictionary[url]) {
			category = ViewCategory.PUBLIC;
		} else if (roleItemSets.has(url)) {
			category = ViewCategory.ROLE_BASED;
		}

		return new CustomViewItem(
			url,
			names[url],
			category,
			customViewDialogStore,
			null,
			selectedCustomViewCnl === url,
			true,
			descriptions?.[url],
		);
	});

	const systemWideItems: CustomViewItem[] = systemWide.map((url) => (new CustomViewItem(
		url,
		names[url],
		ViewCategory.PUBLIC,
		customViewDialogStore,
		null,
		url === selectedCustomViewCnl,
		favoriteSets.has(url),
	)));

	const personalItems: NonGlobalCustomView[] = personal.map(({ name, url, views }) => new NonGlobalCustomView(
		url,
		name,
		views.map((url) => (new CustomViewItem(
			url,
			names[url],
			ViewCategory.PERSONAL,
			customViewDialogStore,
			null,
			url === selectedCustomViewCnl,
			favoriteSets.has(url),
			descriptions?.[url],
		))),
		customViewDialogStore,
	));

	const tempRoleBasedViewMap = new Map<cnl, CustomViewItem>;
	const roleItems: NonGlobalCustomView[] = roles.map(({ name, url, views }) => new NonGlobalCustomView(
		url,
		name,
		views.map((url) => {
			let item = tempRoleBasedViewMap.get(url);

			if (!item) {
				item = new CustomViewItem(
					url,
					names[url],
					ViewCategory.ROLE_BASED,
					customViewDialogStore,
					null,
					url === selectedCustomViewCnl,
					favoriteSets.has(url),
					descriptions?.[url],
				);

				tempRoleBasedViewMap.set(url, item);
			}

			return item;
		}),
		customViewDialogStore,
	));

	const defaultCustomViewUrl = customViewDialogStore.defaults.get(customViewDialogStore.customViewContext, EMPTY_URL, true) || EMPTY_URL;

	// if OOTB is still the default one, show it
	if (defaultCustomViewUrl === EMPTY_URL) {
		names[EMPTY_URL] = customViewDialogStore.loc.resource("ViewDefault");

		systemWideItems.unshift(new CustomViewItem(
			EMPTY_URL,
			names[EMPTY_URL],
			ViewCategory.PUBLIC,
			customViewDialogStore,
			null,
			EMPTY_URL === selectedCustomViewCnl,
			false,
		));
	}

	if (!personalViewsDictionary[userUrl]) {
		// add the current user in if it's not part of the list yet
		const view = new NonGlobalCustomView(
			userUrl,
			userName,
			[],
			customViewDialogStore,
		);

		personalItems.unshift(view);
	}

	const result = new CustomViewStore(personalItems, roleItems, systemWideItems, favoriteItems, customViewDialogStore);

	return result;
}

/** return default util instance */
export function getDefaults(): Defaults {
	const { getRolePreferences, playableRolesStore, sitePreferences, userPreferences } = sessionStore;
	const d = new DefaultsConstructor();

	d.setSitePreferences(sitePreferences);
	d.setUserPreferences(userPreferences);

	playableRolesStore.internalQuery().forEach((item) => {
		const urlParty = playableRolesStore.getIdentity(item);
		const rp = getRolePreferences(urlParty.toString());
		if (rp) {
			d.setRolePreferences(rp);
		}
	});

	return d;
}

/** validate the view name against the current view container and share category */
export function validateUniqueCustomViewName(availableCustomViews: ICustomViewInfoResponse, customViewUrl: cnl, name: string, shareViewCategory: ShareViewWith): boolean {
	const { names, systemWide, roles, personal } = availableCustomViews;

	if (shareViewCategory == ShareViewWith.EVERYONE) {
		for (const viewCnl of systemWide) {
			if (names[viewCnl] === name && viewCnl != customViewUrl) {
				return false;
			}
		}
	} else if (shareViewCategory == ShareViewWith.ME) {
		for (const personalItem of personal) {
			if (personalItem.url !== sessionStore.userUrl) {
				continue;
			}

			for (const viewCnl of personalItem.views) {
				if (names[viewCnl] === name && viewCnl != customViewUrl) {
					return false;
				}
			}
		}
	} else {
		for (const roleItem of roles) {
			for (const viewCnl of roleItem.views) {
				if (names[viewCnl] === name && viewCnl != customViewUrl) {
					return false;
				}
			}
		}
	}

	return true;
}